import { format } from 'date-fns/format';
import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import "react-datepicker/dist/react-datepicker.css"; // Import DatePicker styles
import styled from 'styled-components';
import GenericTable from '../../components/GenericTable';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { PointTransaction } from '../../models/PointTransaction';
import { getTransactionPoints } from '../../redux/entities/userEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';

const PageHeading = styled.h2`
  margin-top: 3rem; /* Space above the heading */
  font-weight: 600;
  color: #4a90e2; /* A soothing blue color */
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-align: center;
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.1); /* Soft shadow for depth */
  letter-spacing: 0.5px;
`;

const TableCard = styled.div`
  background: #ffffff;
  border: 1px solid #d4deea;
  border-radius: 14px;
  box-shadow: 0 8px 20px rgba(20, 34, 66, 0.08);
  padding: 10px 10px 2px;
  overflow: hidden;

  .table {
    margin-bottom: 0.5rem;
    border-collapse: separate;
    border-spacing: 0;
    border: 1px solid #dce4ef;
    border-radius: 10px;
    overflow: hidden;
  }

  .table thead th {
    background: #f1f6fc;
    color: #2b4866;
    border-top: none;
    border-bottom: 1px solid #d3dfed;
    border-right: 1px solid #dbe5f1;
    font-weight: 700;
    font-size: 0.88rem;
    letter-spacing: 0.2px;
    text-transform: uppercase;
    padding: 12px 10px;
  }

  .table thead th:last-child {
    border-right: none;
  }

  .table tbody td {
    border-top: 1px solid #e4ebf3;
    border-right: 1px solid #edf2f8;
    padding: 12px 10px;
    vertical-align: middle;
    color: #223448;
    font-size: 0.93rem;
  }

  .table tbody td:last-child {
    border-right: none;
  }

  .table tbody tr:hover td {
    background: #f7fbff;
  }

  .pagination .page-link {
    border-radius: 8px;
    margin: 0 2px;
    border: 1px solid #d7e3f2;
    color: #375a7f;
    min-width: 36px;
    text-align: center;
  }

  .pagination .page-item.active .page-link {
    background-color: #4a90e2;
    border-color: #4a90e2;
    color: #fff;
  }
`;

const TypePill = styled.span<{ $tone: 'success' | 'danger' | 'info' }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${({ $tone }) =>
    $tone === 'success' ? '#1d6b2f' : $tone === 'danger' ? '#8c2f2f' : '#2d5378'};
  background: ${({ $tone }) =>
    $tone === 'success' ? '#e8f7ec' : $tone === 'danger' ? '#fdeeee' : '#eaf3ff'};
  border: 1px solid ${({ $tone }) =>
    $tone === 'success' ? '#bfe8c9' : $tone === 'danger' ? '#f4c4c4' : '#cfe3ff'};
`;

const PointTransPage: React.FC = () => {
  // Explicitly typing the `selectedDate` state as `Date | null`
  const { showModalStrip } = useModalStrip();
  const dispatch = useAppDispatch();
  const [paginationData, setPaginationData] = useState({
    currentPage: 1,
    pageSize: 20
  });

  const pointTransactions = useAppSelector((state) => state.entities.userReducer.pointTransactions);

  useEffect(() => {
    const offset = paginationData.currentPage - 1;
    dispatch(getTransactionPoints(offset))
      .unwrap()
      .catch((err: Error) => {
        showModalStrip('danger', err.message, 5000);
      });
  }, [dispatch, paginationData.currentPage, showModalStrip]);

  const handlePageChange = (pageNumber: number, pageSize: number) => {
    setPaginationData((prev) => ({
      ...prev,
      currentPage: pageNumber,
      pageSize
    }));
  };

  const columns = [
    { header: 'Date', field: 'date' , render: (rowData: PointTransaction) => {
      return format(rowData.date, 'dd-MM-yyyy')
    }},
    { header: 'Point', field: 'points', render: (rowData: PointTransaction) => {
      return rowData.points.toFixed(2);
    }},
    { header: 'Sender', field: '' , render: (rowData: PointTransaction) => {
      return  rowData?.sender?.name; // Return Min/Max or '-' if data is missing
    }},
    { header: 'Transaction Type', field: 'transactionType', render: (rowData: PointTransaction) => {
      const type = (rowData.transactionType || '').toLowerCase();
      const tone = type.includes('add') || type.includes('credit') || type.includes('in')
        ? 'success'
        : type.includes('revoke') || type.includes('debit') || type.includes('out')
          ? 'danger'
          : 'info';
      return <TypePill $tone={tone}>{rowData.transactionType}</TypePill>;
    }},
    { header: 'Comment', field: 'reason' },
  ];

  return (
    <Container>
      <PageHeading>Points Transactions</PageHeading>

      {/* {transactions.length > 0 && ( */}
        <>
        <TableCard>
          <GenericTable
            columns={columns}
            data={pointTransactions.content || []}
            actions={false}
            pagination={true}
            paginationData={{
              totalElements: pointTransactions.totalElements || 0,
              pageSize: paginationData.pageSize,
              currentPage: paginationData.currentPage
            }}
            onPageChange={handlePageChange}
          />
        </TableCard>
        </>
      {/* )} */}
    </Container>
  );
};

export default PointTransPage;
